import {
  ApiListResponse,
  ApiTokenListResponse,
  CheckInDTO,
  NoVisitReportDTO,
  TokenPageableRequestParams,
  UserMemberAccessLogItem,
  VisitPercentageDTO,
  VisitTotalReportDTO,
} from "@gymflow/types";
import { AxiosInstance } from "axios";

import { createSortParam } from "../createSortParam";
import { SortType } from "./features/types";

export type AccessApiType = ReturnType<typeof accessApi>;

export type GetUserMemberAccessLogRequestParams = {
  userMemberId: string;
} & TokenPageableRequestParams;

const accessApi = (
  axiosInstance: AxiosInstance,
  clubId: number,
) => {
  const clubPrefix = `club/${clubId}/`;

  return {
    find({ page = 0, limit = 10, extraParams = {}, sort }: any) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }
      return axiosInstance.get(`${clubPrefix}access`, config);
    },
    csv(filters: {
      dateFrom?: string;
      dateTo?: string;
      membershipIncluded?: string[];
      onSite?: boolean;
      guest?: boolean;
    }) {
      return axiosInstance.get<string>(`${clubPrefix}access/csv`, {
        params: filters,
      });
    },
    getCheckIn(userMemberId: string) {
      return axiosInstance.get<{
        isCheckedIn: boolean;
      }>(`${clubPrefix}access/check-in/${userMemberId}/status`);
    },
    checkIn(userMemberId: string, payload: { guest: boolean }) {
      return axiosInstance.put<CheckInDTO>(
        `${clubPrefix}access/check-in/${userMemberId}`,
        payload,
      );
    },
    checkOut(userMemberId: string) {
      return axiosInstance.put(`${clubPrefix}access/check-out/${userMemberId}`);
    },
    visitTotalReport({
      dateFrom,
      dateTo,
      sort,
      page,
      size,
    }: {
      dateFrom: string;
      dateTo: string;
      sort?: SortType;
      page?: number;
      size?: number;
    }) {
      const config = {
        params: { dateFrom, dateTo, page, size },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }
      return axiosInstance.get<ApiListResponse<VisitTotalReportDTO>>(
        `${clubPrefix}visit/total`,
        config,
      );
    },
    visitTotalReportCsv(params: { dateFrom: string; dateTo: string }) {
      return axiosInstance.get<string>(`${clubPrefix}visit/total/csv`, {
        params,
      });
    },
    noVisitReport(params: {
      dateFrom: string;
      dateTo: string;
      page?: number;
      size?: number;
    }) {
      return axiosInstance.get<ApiListResponse<NoVisitReportDTO>>(
        `${clubPrefix}visit/no-visit`,
        {
          params,
        },
      );
    },
    noVisitReportCsv(params: { dateFrom: string; dateTo: string }) {
      return axiosInstance.get<string>(`${clubPrefix}visit/no-visit/csv`, {
        params,
      });
    },
    visitPercentage(params: { dateFrom: string; dateTo: string }) {
      return axiosInstance.get<VisitPercentageDTO>(
        `${clubPrefix}visit/percentage`,
        {
          params,
        },
      );
    },
    getUserMemberAccessLog({
      userMemberId,
      limit,
      nextPageToken,
    }: GetUserMemberAccessLogRequestParams) {
      return axiosInstance.get<ApiTokenListResponse<UserMemberAccessLogItem>>(
        `${clubPrefix}user-member-access-log/${userMemberId}`,
        {
          params: {
            limit,
            nextPageToken,
          },
        },
      );
    },
  };
};

export default accessApi;
